import {
  TourLifecycleState,
  LifecycleMarkMetadata,
  getTourLifecycleMarkMetadata,
} from './lifecycleCore';
import { resolveTourId } from './tourUtils';

/**
 * Get the current tour lifecycle state and tour ID.
 * Returns the latest performance mark when available. When no marks exist,
 * falls back to checking URL params, linked tour storage (ONBOARDING_NEXT_TOUR),
 * and CEM multi-pages tour storage (cem-multi-pages-tour): returns INIT if a
 * tour ID is found, or NO_TOUR otherwise.
 */
export function getTourLifecycleDetails(): LifecycleMarkMetadata {
  const markMetadata = getTourLifecycleMarkMetadata();
  if (markMetadata) {
    return markMetadata;
  }
  const tourId = resolveTourId();
  if (tourId) {
    return { state: TourLifecycleState.INIT, tourId };
  }
  return { state: TourLifecycleState.NO_TOUR, tourId: null };
}

/**
 * Check if user is currently in an active tour session.
 * Returns true if the state indicates an ongoing tour (not NO_TOUR, CONFIG_LOAD_ERROR, TOUR_LOAD_CANCELLED, or TOUR_ENDED).
 * Can be used independently across different entries.
 *
 * @param tourId - Optional tour ID to check if the user is in a specific tour session
 */
export function isInTourSession(tourId?: string): boolean {
  const { state, tourId: currentTourId } = getTourLifecycleDetails();

  const nonSessionStates: TourLifecycleState[] = [
    TourLifecycleState.NO_TOUR,
    TourLifecycleState.CONFIG_LOAD_ERROR,
    TourLifecycleState.TOUR_LOAD_CANCELLED,
    TourLifecycleState.TOUR_ENDED,
  ];

  const isInSession = !nonSessionStates.includes(state);
  return tourId ? isInSession && currentTourId === tourId : isInSession;
}

/**
 * Check if a tour is currently active (in loading or active state).
 * Returns true if the state indicates the tour is loading or active.
 * Different from isInTourSession - excludes error/cancelled states.
 *
 * @param tourId - Optional tour ID to check if a specific tour is active
 */
export function isTourCurrentlyActive(tourId?: string): boolean {
  const markMetadata = getTourLifecycleMarkMetadata();

  if (!markMetadata) {
    return false;
  }

  const activeStates: TourLifecycleState[] = [
    TourLifecycleState.CONFIG_LOADING,
    TourLifecycleState.CONFIG_LOADED,
    TourLifecycleState.TOUR_STARTING,
    TourLifecycleState.TOUR_ACTIVE,
  ];

  const { state, tourId: currentTourId } = markMetadata;
  const isTourActive = activeStates.includes(state);
  return tourId ? isTourActive && currentTourId === tourId : isTourActive;
}
